import { useState } from "react";
import { useMediaQuery, useTheme, Box } from "@mui/material";
import TripPlannerPanel from "components/map/TripPlannerPanel";
import MapContainer from "components/map/MapContainer";
import useDirections from "hooks/maps/useDirections";
import { GoogleMap } from "types/map";
import { useAppSelector } from "redux/hooks";
import {
  selectReferenceTrip,
  selectReferenceTripFrom,
  selectReferenceTripDestinations,
} from "redux/slices/shed/shedSlice";
import useMyLittleFranceInit from "hooks/my-little-france/useMyLittleFranceInit";
import useMapShareTripParams from "hooks/maps/useMapShareTripParams";
import useMapDefaultCenter from "hooks/maps/useMapDefaultCenter";
import MapMarkerCurrentPosition from "components/map/MapMarkerCurrentPosition";
import MapMarkersExperience from "components/map/MapMarkersExperience";
import MapItineraryMarkers from "components/my-little-france/MapItineraryMarkers";
import MyLittleFranceCredibilityWidget from "components/my-little-france/MyLittleFranceCredibilityWidget";
import { ROUTE } from "components/base-app/AppRoutes";
import { noop } from "lodash/fp";

/** `/embed/my-little-france` — France rental map with experience markers. */
const MyLittleFranceEmbedPage = () => {
  useMyLittleFranceInit();
  useMapShareTripParams({
    defaultExperienceId: "my-little-france",
    useFranceRentalFleet: true,
  });

  const [map, setMap] = useState<GoogleMap | null>(null);
  const { center: defaultCenter, zoom: defaultZoom } = useMapDefaultCenter();
  const { fromLatitude, fromLongitude } = useAppSelector(selectReferenceTrip);
  const from = useAppSelector(selectReferenceTripFrom);
  const destinations = useAppSelector(selectReferenceTripDestinations);

  useDirections({ from, destinations });

  const theme = useTheme();
  const breakpointXS = useMediaQuery(theme.breakpoints.only("xs"));
  const flexDirection = breakpointXS ? "column" : "row";

  return (
    <Box
      display="flex"
      height="100vh"
      flexDirection={flexDirection}
      sx={{ overflow: "hidden" }}
    >
      <TripPlannerPanel
        shareRoute={ROUTE.MY_LITTLE_FRANCE_EMBED}
        includeExperience
      />
      <Box flex={1} minWidth={0} minHeight={0} position="relative">
        <MapContainer
          boundaryWidth="100%"
          boundaryHeight="100%"
          map={map}
          center={
            fromLatitude && fromLongitude
              ? {
                  lat: fromLatitude ?? 0,
                  lng: fromLongitude ?? 0,
                }
              : defaultCenter
          }
          onMapInitialized={setMap}
          zoom={defaultZoom}
          disableDefaultUI
          zoomControl={false}
          suppressDirectionMarkers
        >
          <>
            <MapMarkerCurrentPosition map={map} />
            <MapItineraryMarkers
              map={map}
              from={from}
              destinations={destinations}
            />
            <MapMarkersExperience map={map} onCardClick={noop} />
            <MyLittleFranceCredibilityWidget />
          </>
        </MapContainer>
      </Box>
    </Box>
  );
};

export default MyLittleFranceEmbedPage;
